import { useState, useEffect } from 'react';
import axios from 'axios';

// ===================== MUI =====================
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  IconButton,
} from '@mui/material';

// ===================== COMPONENTS =====================
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import Iconify from '../components/Iconify';
import RenderArrayFields from '../components/RenderArrayFields';
// import RenderArrayFields from '../components/RenderArrayFields';
// ===================== ABOUT PAGE SECTIONS =====================
import AboutPageListToolbar from '../sections/reports/aboutPage/aboutPageListToolbar';
import AboutPageListHead from '../sections/reports/aboutPage/aboutPageListHead';
import AboutPageMoreMenu from '../sections/reports/aboutPage/aboutPageMoreMenu';

// ================================================================

const TABLE_HEAD = [
  { id: 'title', label: 'Title' },
  { id: 'description', label: 'Description' },
  { id: 'aboutTitle', label: 'About Title' },
  { id: 'aboutDescription', label: 'About Description' },
  { id: 'expertiseTitle', label: 'Expertise Title' },
  { id: 'expertiseTitleContents', label: 'Expertise' },
  { id: 'action', label: 'Action' },
];

// ================================================================

export default function About() {
  // ===================== DATA =====================
  const [aboutPages, setAboutPages] = useState([]);

  // ===================== TABLE =====================
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filterName, setFilterName] = useState('');
  const [selected, setSelected] = useState([]);

  // ===================== MODALS =====================
  const [openModal, setOpenModal] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewData, setViewData] = useState(null);

  // ===================== FORM =====================
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [aboutTitle, setAboutTitle] = useState('');
  const [aboutDescription, setAboutDescription] = useState('');
  const [expertiseTitle, setExpertiseTitle] = useState('');
  const [expertiseTitleContents, setExpertiseTitleContents] = useState([]);

  // ================================================================

  useEffect(() => {
    fetchAboutPage();
  }, []);

  // ===================== API =====================

  const fetchAboutPage = async () => {
    const res = await axios.get(
      'https://cpccbe.org/api/aboutpage/get-aboutpage'
    );

    console.log('Fetched About Page Data:', res.data);
    // backend returns single object → convert to array for table
    setAboutPages([res.data]);
  };

  // ===================== HELPERS =====================

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setAboutTitle('');
    setAboutDescription('');
    setExpertiseTitle('');
    setExpertiseTitleContents([]);
  };

  const handleOpenModal = (row = null) => {
    if (row) {
      setTitle(row.title);
      setDescription(row.description);
      setAboutTitle(row.aboutTitle);
      setAboutDescription(row.aboutDescription);
      setExpertiseTitle(row.expertiseTitle);
      setExpertiseTitleContents(row.expertiseTitleContents || []);
    } else {
      resetForm();
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    resetForm();
    setOpenModal(false);
  };

  // ===================== CRUD =====================

  const handleCreate = async () => {
    const payload = {
      title,
      description,
      aboutTitle,
      aboutDescription,
      expertiseTitle,
      expertiseTitleContents,
    };

    await axios.post(
      'https://cpccbe.org/api/aboutpage/create-aboutpage',
      payload
    );

    fetchAboutPage();
    handleCloseModal();
  };

  const handleUpdate = async () => {
    const payload = {
      title,
      description,
      aboutTitle,
      aboutDescription,
      expertiseTitle,
      expertiseTitleContents,
    };

    // Updated to check the new payload length instead of existing state
    if (expertiseTitleContents.length <= 6) {
      await axios.put(
        `https://cpccbe.org/api/aboutpage/update-aboutpage/${aboutPages[0]._id}`,
        payload
      );
    } else {
      alert("You can only have up to Six expertise items.");
    }

    fetchAboutPage();
    handleCloseModal();
  };

   const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await axios.delete(`https://cpccbe.org/api/aboutpage/delete-aboutpage/${id}`);  // Corrected endpoint
        fetchAboutPage();
      } catch (error) {
        console.error('Error deleting about page:', error);
      }
    }
  };


  // ===================== FILTER =====================

  const filteredData = aboutPages.filter((item) =>
    item.title?.toLowerCase().includes(filterName.toLowerCase())
  );

  const isNotFound = filteredData.length === 0;

  const hasData = aboutPages.length > 0;

  // ================================================================

  return (
    <Page title="About Us">
      <Container>
        <Stack direction="row" justifyContent="space-between" mb={5}>
          <Typography variant="h4">About Us</Typography>

          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            disabled={hasData}
            onClick={() => handleOpenModal()}
          >
            Create About Page
          </Button>
        </Stack>

        <Card>
          <AboutPageListToolbar
            filterName={filterName}
            onFilterName={(e) => setFilterName(e.target.value)}
            numSelected={selected.length}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <AboutPageListHead headLabel={TABLE_HEAD} />

                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <TableRow key={row._id}>
                        <TableCell padding="checkbox">
                          <IconButton
                            onClick={() => {
                              setViewData(row);
                              setViewModalOpen(true);
                            }}
                          >
                            <Iconify icon="eva:eye-fill" />
                          </IconButton>
                        </TableCell>
                        <TableCell>{row.title}</TableCell>
                        <TableCell>{row.description}</TableCell>
                        <TableCell>{row.aboutTitle}</TableCell>
                        <TableCell>{row.aboutDescription}</TableCell>
                        <TableCell>{row.expertiseTitle}</TableCell>

                        <TableCell>
                          {row.expertiseTitleContents?.map((item, index) => (
                            <div key={index}>
                              {index + 1}. {item.title}
                            </div>
                          ))}
                        </TableCell>

                        <TableCell align="right">


                          <AboutPageMoreMenu
                            onEdit={() => handleOpenModal(row)}
                            onDelete={() => { handleDelete(row._id); }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}

                  {isNotFound && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={aboutPages.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) =>
              setRowsPerPage(+e.target.value)
            }
          />
        </Card>

        {/* ===================== CREATE / EDIT MODAL ===================== */}
        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {hasData ? 'Edit About Page' : 'Create About Page'}
          </DialogTitle>

          <DialogContent dividers>
            <TextField
              fullWidth
              margin="dense"
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <TextField
              fullWidth
              margin="dense"
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <TextField
              fullWidth
              margin="dense"
              label="About Title"
              value={aboutTitle}
              onChange={(e) => setAboutTitle(e.target.value)}
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              margin="dense"
              label="About Description"
              value={aboutDescription}
              onChange={(e) => setAboutDescription(e.target.value)}
            />

            <TextField
              fullWidth
              margin="dense"
              label="Expertise Title"
              value={expertiseTitle}
              onChange={(e) => setExpertiseTitle(e.target.value)}
            />

            <RenderArrayFields
              label="Expertise Contents"
              data={expertiseTitleContents}
              setData={setExpertiseTitleContents}
              fields={['title', 'content']}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>

            {hasData ? (
              <Button onClick={handleUpdate}>Update</Button>
            ) : (
              <Button onClick={handleCreate}>Create</Button>
            )}
          </DialogActions>
        </Dialog>

        {/* ===================== VIEW MODAL ===================== */}
        <Dialog
          open={viewModalOpen}
          onClose={() => setViewModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>About Page Details</DialogTitle>

          <DialogContent dividers>
            {viewData && (
              <Box>
                <Typography variant="subtitle2">Title</Typography>
                <Typography>{viewData.title}</Typography>

                <Typography variant="subtitle2" mt={2}>
                  About
                </Typography>
                <Typography
                  dangerouslySetInnerHTML={{
                    __html: viewData.aboutDescription,
                  }}
                />

                <Typography variant="subtitle2" mt={2}>
                  Expertise
                </Typography>
                {viewData.expertiseTitleContents?.map((item, index) => (
                  <Typography key={index}>
                    {index + 1}. {item.title} — {item.content}
                  </Typography>
                ))}
              </Box>
            )}
          </DialogContent>
         
          <DialogActions>
            <Button onClick={() => setViewModalOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}